import { Room } from '../types/room'
import config from './config'

interface CacheEntry {
  data: Room
  timestamp: number
  expiresAt: number
}

interface RoomCache {
  [roomId: string]: CacheEntry
}

// Cache configuration
const CACHE_TTL = 5 * 60 * 1000 // 5 minutes - data is "fresh" for this long
const STALE_TTL = 30 * 60 * 1000 // 30 minutes - data is "stale but usable" for this long
const STORAGE_KEY = 'brc_room_cache'
const CACHE_VERSION_KEY = 'brc_room_cache_version'
const CACHE_VERSION = 2 // Increment this to invalidate old cache
const isDevelopment = config.environment !== 'production'

class RoomCacheService {
  private memoryCache: RoomCache = {}
  private initialized = false

  constructor() {
    this.loadFromStorage()
  }

  private loadFromStorage() {
    if (this.initialized) return
    this.initialized = true
    
    try {
      // Check cache version - clear if outdated
      const storedVersion = localStorage.getItem(CACHE_VERSION_KEY)
      if (storedVersion !== String(CACHE_VERSION)) {
        if (isDevelopment) {
          console.log(`📦 Cache version changed (${storedVersion} -> ${CACHE_VERSION}), clearing old cache`)
        }
        localStorage.removeItem(STORAGE_KEY)
        localStorage.setItem(CACHE_VERSION_KEY, String(CACHE_VERSION))
        return
      }
      
      const stored = localStorage.getItem(STORAGE_KEY)
      if (stored) {
        const parsed = JSON.parse(stored) as RoomCache
        const now = Date.now()
        
        // Only load entries that haven't expired AND have complete data
        Object.entries(parsed).forEach(([roomId, entry]) => {
          if (entry.timestamp + STALE_TTL > now) {
            // Validate that cached room has complete player data with predictions
            const hasCompleteData = entry.data.players?.every((player: any) => 
              player.predictions !== undefined && Array.isArray(player.predictions)
            )
            
            // Only load if complete or has no players (new room)
            if (hasCompleteData || !entry.data.players?.length) {
              this.memoryCache[roomId] = entry
            }
          }
        })
        
        if (isDevelopment) {
          console.log(`📦 Loaded ${Object.keys(this.memoryCache).length} complete rooms from cache`)
        }
      }
    } catch (error) {
      console.warn('Failed to load room cache from storage:', error)
    }
  }

  private saveToStorage() {
    try {
      // Only save recent entries (last 20 rooms) that have complete data
      const entries = Object.entries(this.memoryCache)
        .filter(([_, entry]) => {
          // Only save rooms with complete player data
          return entry.data.players?.every((player: any) => 
            player.predictions !== undefined && Array.isArray(player.predictions)
          ) || !entry.data.players?.length
        })
        .sort((a, b) => b[1].timestamp - a[1].timestamp)
        .slice(0, 20)
      
      const toSave: RoomCache = {}
      entries.forEach(([roomId, entry]) => {
        toSave[roomId] = entry
      })
      
      localStorage.setItem(STORAGE_KEY, JSON.stringify(toSave))
      localStorage.setItem(CACHE_VERSION_KEY, String(CACHE_VERSION))
    } catch (error) {
      console.warn('Failed to save room cache to storage:', error)
    }
  }

  /**
   * Get cached room data
   * Returns { data, isFresh, isStale } or null if not cached
   * Only returns rooms that have complete data (players with predictions)
   */
  get(roomId: string): { data: Room; isFresh: boolean; isStale: boolean } | null {
    const entry = this.memoryCache[roomId]
    if (!entry) return null

    const now = Date.now()
    const age = now - entry.timestamp
    
    // Check if completely expired (beyond stale TTL)
    if (age > STALE_TTL) {
      delete this.memoryCache[roomId]
      return null
    }

    // IMPORTANT: Validate that cached room has complete data (players with predictions)
    // This prevents showing incomplete data that causes "invalid predictions: undefined" errors
    const hasCompleteData = entry.data.players?.every((player: any) => 
      player.predictions !== undefined && Array.isArray(player.predictions)
    )
    
    if (!hasCompleteData && entry.data.players?.length > 0) {
      // Cached room is incomplete - remove it and return null
      if (isDevelopment) {
        console.log(`📦 Discarding incomplete cache for room ${roomId}`)
      }
      delete this.memoryCache[roomId]
      return null
    }

    return {
      data: entry.data,
      isFresh: age < CACHE_TTL,
      isStale: age >= CACHE_TTL
    }
  }

  /**
   * Store room data in cache
   */
  set(roomId: string, data: Room) {
    const now = Date.now()
    this.memoryCache[roomId] = {
      data,
      timestamp: now,
      expiresAt: now + CACHE_TTL
    }
    
    // Save to localStorage asynchronously
    setTimeout(() => this.saveToStorage(), 0)
    
    if (isDevelopment) {
      console.log(`📦 Cached room ${roomId} (${data.name})`)
    }
  }

  /**
   * Update specific fields in cached room
   */
  update(roomId: string, updates: Partial<Room>) {
    const entry = this.memoryCache[roomId]
    if (entry) {
      entry.data = { ...entry.data, ...updates }
      entry.timestamp = Date.now() // Refresh timestamp
      this.saveToStorage()
    }
  }

  /**
   * Invalidate cache for a room
   */
  invalidate(roomId: string) {
    delete this.memoryCache[roomId]
    this.saveToStorage()
  }

  /**
   * Clear all cache
   */
  clear() {
    this.memoryCache = {}
    localStorage.removeItem(STORAGE_KEY)
  }

  /**
   * Prefetch rooms list - ONLY caches rooms that have complete data (with predictions)
   * Rooms from the list endpoint don't have predictions, so we skip those
   * to avoid breaking the room detail view
   */
  async prefetch(rooms: Room[]) {
    const now = Date.now()
    let prefetchedCount = 0
    
    rooms.forEach(room => {
      const roomId = room.id || (room as any)._id
      if (!roomId) return
      
      // IMPORTANT: Only cache rooms that have complete player data WITH predictions
      // Rooms from /rooms list endpoint don't have predictions - don't cache those
      // as it would cause "invalid predictions: undefined" errors in room detail view
      const hasCompleteData = room.players?.some((player: any) => 
        player.predictions !== undefined && Array.isArray(player.predictions)
      )
      
      if (!hasCompleteData) {
        // This room is from the list endpoint (no predictions) - skip caching
        return
      }
      
      // Only prefetch if not already cached or cache is stale
      const existing = this.memoryCache[roomId]
      if (!existing || (now - existing.timestamp) > CACHE_TTL) {
        this.memoryCache[roomId] = {
          data: room,
          timestamp: now,
          expiresAt: now + CACHE_TTL
        }
        prefetchedCount++
      }
    })
    
    if (isDevelopment && prefetchedCount > 0) {
      console.log(`📦 Prefetched ${prefetchedCount} complete rooms into cache`)
    }
    
    // Save to storage asynchronously
    setTimeout(() => this.saveToStorage(), 0)
  }

  /**
   * Check if a room is cached (without returning data)
   */
  has(roomId: string): boolean {
    const entry = this.memoryCache[roomId]
    if (!entry) return false
    
    const age = Date.now() - entry.timestamp
    return age <= STALE_TTL
  }

  /**
   * Get cache stats for debugging
   */
  getStats() {
    const entries = Object.entries(this.memoryCache)
    const now = Date.now()
    
    return {
      totalEntries: entries.length,
      freshEntries: entries.filter(([_, e]) => now - e.timestamp < CACHE_TTL).length,
      staleEntries: entries.filter(([_, e]) => now - e.timestamp >= CACHE_TTL && now - e.timestamp < STALE_TTL).length,
      rooms: entries.map(([id, e]) => ({
        id,
        name: e.data.name,
        age: Math.round((now - e.timestamp) / 1000) + 's'
      }))
    }
  }
}

// Singleton instance
const roomCacheService = new RoomCacheService()
export default roomCacheService

